import { checkOnlineStatus } from "../onlineStatus.js";
import { reportIfQuotaError } from "./quotaSignal.js";

// Debug logging system for PWA debugging
const DEBUG_KEY = "artemis_debug_logs";
const MAX_LOGS = 500;

export class DebugLogger {
  static log(level, message, context = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    try {
      const logs = this.getLogs();
      logs.unshift(logEntry);

      // Keep only the latest MAX_LOGS entries
      if (logs.length > MAX_LOGS) {
        logs.splice(MAX_LOGS);
      }

      localStorage.setItem(DEBUG_KEY, JSON.stringify(logs));
    } catch (error) {
      // If localStorage is full, clear old logs and try again
      try {
        localStorage.removeItem(DEBUG_KEY);
        localStorage.setItem(DEBUG_KEY, JSON.stringify([logEntry]));
      } catch (e) {
        // Can't store logs, fail silently
      }
    }
  }

  static error(message, context = {}) {
    this.log("ERROR", message, context);
  }

  static warn(message, context = {}) {
    this.log("WARN", message, context);
  }

  static info(message, context = {}) {
    this.log("INFO", message, context);
  }

  static getLogs() {
    try {
      const logs = localStorage.getItem(DEBUG_KEY);
      return logs ? JSON.parse(logs) : [];
    } catch {
      return [];
    }
  }

  static clearLogs() {
    try {
      localStorage.removeItem(DEBUG_KEY);
    } catch {
      // Fail silently
    }
  }

  static async exportLogs() {
    const logs = this.getLogs();
    const online = await checkOnlineStatus();
    const exportData = {
      exportTime: new Date().toISOString(),
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        online,
      },
      logs,
    };

    return JSON.stringify(exportData, null, 2);
  }

  static getStorageInfo() {
    try {
      if ("storage" in navigator && "estimate" in navigator.storage) {
        return navigator.storage.estimate().then((estimate) => ({
          quota: estimate.quota,
          usage: estimate.usage,
          available: estimate.quota - estimate.usage,
          usagePercent: Math.round((estimate.usage / estimate.quota) * 100),
        }));
      }
    } catch {
      return Promise.resolve(null);
    }
    return Promise.resolve(null);
  }
}

// Track critical errors automatically
window.addEventListener("error", (event) => {
  reportIfQuotaError(event.error);
  DebugLogger.error("JavaScript Error", {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack,
  });
});

window.addEventListener("unhandledrejection", (event) => {
  reportIfQuotaError(event.reason);
  DebugLogger.error("Unhandled Promise Rejection", {
    reason: event.reason?.toString() || "Unknown reason",
    stack: event.reason?.stack,
  });
});
